/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Ref, VNode } from 'preact';
import { useContext, useMemo, useRef, useState } from 'preact/hooks';
import { BaseLegend } from '#PRIVATE_BaseLegend/BaseLegend';
import { useVisEvent } from '#hooks/PRIVATE_useVisEvents/useVisEvents';
import { LegendProps, LegendDragStartDetail } from './legend.type';
import { getLegendNavUtils } from './legendNavUtils';
import {
  getAriaProps,
  getGridNums,
  getIsLegendItemActionable,
  getItemInfo,
  getLegendTextStyle,
  isEqualItem
} from '#utils/PRIVATE_legendUtils/legendUtils';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { useLegendDatatip } from './useLegendDatatip';
import { useItemFocus } from '#hooks/PRIVATE_useVisEvents';
import { styles } from '#PRIVATE_BaseLegend/themes/LegendStyles.css';
import { useCssVars } from '#hooks/PRIVATE_useCssVars';
import { legendVars, getKeyUpHandler } from '#utils/PRIVATE_legendUtils/legendUtils';
import { classNames } from '#utils/UNSAFE_classNames';
import { useTestId } from '#hooks/UNSAFE_useTestId';
import { useLegendContextMenu } from '#PRIVATE_BaseLegend/useLegendContextMenu';
import {
  ItemFocusDetail,
  ItemActionDetail,
  ItemInfo,
  LegendItemData
} from '#utils/UNSAFE_visTypes/legend';
import { useVisTouchResponse } from '#hooks/PRIVATE_useVisTouchResponse';
import { merge } from '@oracle/oraclejet-internal-utilities/stringUtils';
import { forwardRef, useImperativeHandle } from 'preact/compat';
import { TrackResizeContainer } from '#UNSAFE_TrackResizeContainer';
import { useTextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { _getPreferredSize, getLinearLegendLayoutInfo } from './getLegendLayout';
import { useLegendDnd } from '#hooks/PRIVATE_useLegendDnD/useLegendDnd';
import { styles as VisDndStyles } from '#hooks/PRIVATE_useVisDnd/themes/VisDndStyles.css';
import { LegendRenderedContext } from '#hooks/UNSAFE_useLegendPreferredSize';

export type SizeHandle = {
  _getPreferredSize: (
    _width: number,
    _height: number
  ) => { width: number; height: number } | undefined;
};

type ForwardRefLegend = <K extends string | number, D extends LegendItemData<K>>(
  props: LegendProps<K, D> & { ref?: Ref<SizeHandle> }
) => VNode | null;

/**
 * A legend displays an interactive description of symbols, colors, etc., used in graphical information representations.
 */
export const Legend = forwardRef(
  <K extends string | number, D extends LegendItemData<K>>(
    {
      orientation = 'horizontal',
      items,
      isReadOnly = true,
      onItemAction,
      onItemHover,
      onItemFocus,
      testId,
      contextMenuConfig,
      hideAndShowBehavior = 'off',
      hoverBehavior = 'none',
      halign = 'start',
      valign = 'top',
      ...props
    }: LegendProps<K, D>,
    ref: Ref<SizeHandle> = null
  ) => {
    // Use the context
    const { isGetPreferredSizeReady, width, height } = useContext(LegendRenderedContext);
    const { textMeasureContent, getTextDimensions } = useTextDimensions();
    const { resolvedVars, cssContent } = useCssVars(legendVars);

    const legendFontStylesObj = getLegendTextStyle(
      {
        fontStyle: props.textFontStyle,
        fontSize: props.textFontSize?.toString(),
        fontWeight: props.textFontWeight?.toString(),
        fontFamily: props.textFontFamily
      },
      resolvedVars
    );
    const rootRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(
      ref!,
      () => ({
        _getPreferredSize: (_width: number, _height: number) => {
          return _getPreferredSize(
            _width,
            _height,
            items,
            resolvedVars,
            orientation,
            legendFontStylesObj,
            props.symbolWidth,
            props.symbolHeight,
            getTextDimensions
          );
        }
      }),
      [
        getTextDimensions,
        resolvedVars,
        orientation,
        legendFontStylesObj,
        props.symbolWidth,
        props.symbolHeight,
        items
      ]
    );

    if (
      getTextDimensions &&
      resolvedVars['unit'] != undefined &&
      resolvedVars['fontSize'] != undefined
    ) {
      isGetPreferredSizeReady?.(true);
    }

    const testIdProps = useTestId(testId);
    const isTestEnv = testIdProps['data-testid'] !== undefined;
    const rootDimsRef = useRef<DOMRect>();
    const { getDetailFromInfo, getPrevItemInfo, getNextItemInfo } = getLegendNavUtils<K, D>(items);

    const isLegendItemActionable = getIsLegendItemActionable(items, isReadOnly);
    const hasData = items.length > 0;

    const legendKeyUpHandler = (
      triggerOnAction: () => void,
      updatefocusedItemInfo: (itemInfo: ItemInfo) => void,
      focusedItemInfo: ItemInfo
    ) =>
      getKeyUpHandler(
        triggerOnAction,
        updatefocusedItemInfo,
        focusedItemInfo,
        props.hiddenIds != null
      );

    const { touchResponse, touchResponseStyle } = useVisTouchResponse({
      type: 'touchStart'
    });
    const { focusedItemInfo, hoveredItemInfo, onContextMenuDismissed, activeId, eventsProps } =
      useVisEvent<ItemInfo, ItemActionDetail<K, D>, ItemFocusDetail<K, D>, D>(
        touchResponse,
        !isReadOnly,
        isLegendItemActionable,
        legendKeyUpHandler,
        getDetailFromInfo,
        getPrevItemInfo,
        getNextItemInfo,
        onItemAction,
        onItemHover,
        onItemFocus,
        isEqualItem,
        getItemInfo,
        { itemIdx: 0, sectionIdx: 0 },
        props.hiddenIds != null
      );

    const { focusedItemRef } = useItemFocus(focusedItemInfo);
    const [datatipDisabled, setDatatipDisabled] = useState(false);
    const {
      datatipContent,
      datatipProps: { 'aria-describedby': datatipAriaDescribedby, ...datatipProps }
    } = useLegendDatatip({
      items,
      datatip: props.datatip,
      rootDimsRef,
      focusedItemInfo,
      focusedItemRef,
      hoveredItemInfo,
      touchResponse,
      isDisabled: datatipDisabled
    });

    const ariaProps = getAriaProps<K>(
      isReadOnly,
      props['aria-label'],
      props.hiddenIds,
      merge([props['aria-describedby'], datatipAriaDescribedby]),
      props['aria-labelledby']
    );

    const { contextMenuContent, contextMenuProps } = useLegendContextMenu<D>({
      rootRef,
      contextMenuConfig: hasData ? contextMenuConfig : undefined,
      focusedItemInfo,
      items,
      isReadOnly,
      onContextMenuDismissed
    });

    const [dragOver, setDragOver] = useState<boolean>();

    const { draggable, dndProps, dndContent } = useLegendDnd<K, D, LegendDragStartDetail<D>>({
      onDrag: props.onDrag,
      onDragStart: props.onDragStart,
      onDragEnd: props.onDragEnd,
      onDragEnter: props.onDragEnter,
      onDragLeave: props.onDragLeave,
      onDragOver: props.onDragOver,
      onDrop: props.onDrop,
      items,
      setDragOver,
      setDatatipDisabled,
      itemDraggable: props.itemDraggable
    });

    const mergedProps = mergeProps(eventsProps, datatipProps, contextMenuProps, dndProps);
    const { legendStyle, renderedLegendStyle } = styles;

    return (
      <>
        {!isGetPreferredSizeReady && (
          <TrackResizeContainer
            width={width ? `${width}px` : '100%'}
            height={height ? `${height}px` : '100%'}
            class={classNames([
              styles.flexStyle,
              styles[`${halign}HAlign`],
              styles[`${valign}VAlign`]
            ])}>
            {function _(width) {
              const layoutInfo = useMemo(
                () =>
                  getLinearLegendLayoutInfo<K>({
                    width,
                    getTextDimensions,
                    resolvedVars,
                    orientation,
                    legendFontStylesObj,
                    symbolWidth: props.symbolWidth,
                    symbolHeight: props.symbolHeight,
                    items
                  }),
                [width]
              );
              const { itemsPerCol, itemsPerRow } = getGridNums(items.length, layoutInfo);
              return (
                <>
                  <div
                    ref={rootRef}
                    tabIndex={isReadOnly ? undefined : 0}
                    role="application"
                    draggable={draggable}
                    class={classNames([
                      legendStyle,
                      renderedLegendStyle,
                      dragOver
                        ? VisDndStyles.dndDragOverHTMLStyle
                        : dragOver === false
                        ? VisDndStyles.dndRejectedStyle
                        : undefined
                    ])}
                    style={touchResponseStyle}
                    {...testIdProps}
                    {...ariaProps}
                    {...mergedProps}
                    aria-activedescendant={activeId}>
                    <BaseLegend
                      {...props}
                      focusedItemRef={focusedItemRef}
                      items={items}
                      isReadOnly={isReadOnly}
                      orientation={orientation}
                      sectionIdx={0}
                      itemsPerRow={itemsPerRow}
                      itemsPerCol={itemsPerCol}
                      itemWidth={layoutInfo?.isWrapped ? layoutInfo?.maxItemWidth : undefined}
                      focusedItemInfo={focusedItemInfo}
                      hoveredItemInfo={hoveredItemInfo}
                      activeId={activeId}
                      hideAndShowBehavior={hideAndShowBehavior}
                      hoverBehavior={hoverBehavior}
                      isTestEnv={isTestEnv}
                    />
                  </div>
                  {datatipContent}
                  {contextMenuContent}
                  {dndContent}
                </>
              );
            }}
          </TrackResizeContainer>
        )}
        {textMeasureContent}
        {cssContent}
      </>
    );
  }
) as ForwardRefLegend;
